import React, { FC, useState } from 'react'

import Fade from '@mui/material/Fade'
import Button from '@mui/material/Button'
import PersonIcon from '@mui/icons-material/Person'
import { PopperPlacementType } from '@mui/material/Popper'
import ClickAwayListener from '@mui/base/ClickAwayListener'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

import { AppIcon } from '@app/components/AppIcon'
import { logout } from '@app/store/reducers/auth/actions'
import { useAppDispatch } from '@app/hooks/useAppDispatch'
import { useTypedSelector } from '@app/hooks/useTypedSelector'
import { selectUserId } from '@app/store/reducers/user/selectors'
import About from '@app/pages/Dashboard/SideBar/components/aboutComponent'
import SidebarNavTab from '@app/pages/Dashboard/SideBar/components/SidebarNavTab/SidebarNavTab'
import SidebarNavLink from '@app/pages/Dashboard/SideBar/components/SidebarNavLink'

import {
	UserActionsButton,
	UserActionsList,
	UserActionsPopper,
	UsersActionsDivider,
	UserActionsBox,
} from './styled.js'

const SidebarUserActions: FC = () => {
	const userID = useTypedSelector(selectUserId)
	const dispatch = useAppDispatch()
	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
		null
	)
	const [open, setOpen] = React.useState(false)
	const [placement, setPlacement] = React.useState<PopperPlacementType>()
	const [arrowEl, setArrowEl] = React.useState<HTMLSpanElement | null>(null)
	const [container, setContainer] = React.useState<HTMLDivElement | null>(
		null
	)
	const [aboutModalOpen, setAboutModalOpen] = useState(false)

	const handleClick =
		(newPlacement: PopperPlacementType) =>
		(event: React.MouseEvent<HTMLButtonElement>) => {
			setAnchorEl(event.currentTarget)
			setOpen(prev => placement !== newPlacement || !prev)
			setPlacement(newPlacement)
		}
	const handleClickAway = (event: MouseEvent | TouchEvent): void => {
		if (anchorEl?.contains(event.target as Element)) {
			return
		}
		setOpen(false)
	}

	const handleClickMyProfile: React.MouseEventHandler<
		HTMLAnchorElement
	> = () => {
		setOpen(false)
	}

	const handleOpenAboutModalEvent = (): void => {
		setOpen(false)
		setAboutModalOpen(true)
	}
	const handleCloseAboutModalEvent = (): void => {
		setAboutModalOpen(false)
	}
	const handleLogout = (): void => {
		dispatch(logout())

		setOpen(false)
	}

	return (
		<UserActionsBox ref={setContainer} open={open}>
			<UserActionsButton onClick={handleClick('right-end')}>
				<PersonIcon color={'inherit'} fontSize={'small'} />
			</UserActionsButton>

			{aboutModalOpen && (
				<About onCloseModal={handleCloseAboutModalEvent} />
			)}

			<ClickAwayListener
				mouseEvent='onMouseDown'
				onClickAway={handleClickAway}
			>
				<UserActionsPopper
					container={container}
					open={open}
					anchorEl={anchorEl}
					placement={placement}
					className={'popper'}
					modifiers={[
						{
							name: 'arrow',
							enabled: true,
							options: {
								element: arrowEl,
							},
						},
						{
							name: 'offset',
							options: {
								offset: [34, 34],
							},
						},
					]}
					transition
				>
					{({ TransitionProps }) => (
						<Fade {...TransitionProps}>
							<UserActionsList elevation={0}>
								<span
									ref={
										setArrowEl as React.Ref<HTMLSpanElement>
									}
									className={'arrow'}
								/>

								<SidebarNavLink
									startIconSlot={
										<AppIcon
											type={'account'}
											style={{ marginRight: '1rem' }}
										/>
									}
									to={`/users/${userID}`}
									onClick={handleClickMyProfile}
									label={'My profile'}
									sx={{ mb: 3, pl: 6.5 }}
								/>

								<SidebarNavTab
									component={Button}
									onClick={handleOpenAboutModalEvent}
									sx={{ pl: 6 }}
								>
									<ErrorOutlineIcon
										sx={{
											fontSize: '1.75rem',
											mr: '0.85rem',
										}}
									/>
									About
								</SidebarNavTab>

								<UsersActionsDivider />

								<SidebarNavTab
									component={Button}
									onClick={handleLogout}
									sx={{ pl: 6.5 }}
								>
									<AppIcon
										type={'logout'}
										style={{ marginRight: '1rem' }}
									/>
									Sign out
								</SidebarNavTab>
							</UserActionsList>
						</Fade>
					)}
				</UserActionsPopper>
			</ClickAwayListener>
		</UserActionsBox>
	)
}

export default SidebarUserActions
